import { gql } from "@apollo/client";
import { Intent, TooltipProps } from "@blueprintjs/core";
import {
  CREATE_DATA_CONNECTION_HEX_VERSION_LINK,
  DataConnectionHexVersionLinkId,
  DataConnectionId,
  DataSourceTableConfig,
  DataSourceTableId,
  getDataframeDisplayName,
  uuid,
} from "@hex/common";
import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";

import { HexButton } from "../../../../hex-components/HexButton.js";
import { HexSpinner } from "../../../../hex-components/HexSpinner.js";
import { HexTooltip } from "../../../../hex-components/HexTooltip.js";
import {
  importedDataConnectionIdsSetSelector,
  useDataConnectionHexVersionLinksSelector,
} from "../../../../hex-version-multiplayer/state-hooks/dataConnectionHexVersionLinkStateHooks.js";
import { useProjectVersionEditable } from "../../../../hooks/sessionOrProjectEditableHooks.js";
import { useTerminology } from "../../../../hooks/useTerminology.js";
import { PillThemes } from "../../../../theme/common/theme.js";
import { useHexVersionAOContext } from "../../../../util/hexVersionAOContext.js";
import { trackCounterEvent } from "../../../../util/trackEvent.js";
import { useDataSourceTableByIdQuery } from "../../../data/schema-browser-v2/queries.generated.js";
import { WarehouseTableIcon, WarningIcon } from "../../../icons/CustomIcons.js";
import { useTableNodeIdPathGetter } from "../../renderers/sql/sqlCellHooks.js";

import {
  DataPillTooltipContent,
  DataPillTooltipContentProps,
  DataPillTooltipContentWrapper,
} from "./DataPillTooltipContent.js";
import { useGetDataConnectionInfoForDataSourceTooltipQuery } from "./DataSourceTablePill.generated.js";

gql`
  query GetDataConnectionInfoForDataSourceTooltip(
    $dataConnectionId: DataConnectionId!
  ) {
    dataConnectionById(dataConnectionId: $dataConnectionId) {
      id
      connectionType
      isMultiDatabase
    }
  }
`;

const DataSourceTableWrapper = styled.div<{ pillTheme?: keyof PillThemes }>`
  ${({ pillTheme, theme }) => theme.pill[pillTheme ?? "GRAY"].css};
  gap: 4px;
  display: inline-flex;
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  align-items: center;
  overflow: hidden;
`;

const StyledWarningIcon = styled(WarningIcon)<{ intent: Intent }>`
  svg {
    color: ${({ intent, theme }) => theme.button.default[intent].fontColor};
  }
  &&&& {
    margin: 0;
  }
`;

const DataSourceTableIcon = styled(WarehouseTableIcon)`
  &&&& {
    margin: 0;
  }
`;

const TableName = styled.span`
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  overflow-wrap: break-word;
  font-weight: ${({ theme }) => theme.fontWeight.MEDIUM};
`;

interface DataSourceTablePillWithTooltipProps
  extends Pick<DataPillTooltipContentProps, "onPopoverDismiss"> {
  dataSourceTableConfig: DataSourceTableConfig;
  onOpenDataBrowser: ({
    dataConnectionId,
    nodeIdPath,
    tableId,
  }: {
    dataConnectionId: DataConnectionId | null;
    tableId: DataSourceTableId | null;
    nodeIdPath: string[] | null;
  }) => void;
  tooltipProps?: Pick<
    TooltipProps,
    "disabled" | "hoverOpenDelay" | "position" | "interactionKind"
  >;
}

export const DataSourceTablePillWithTooltip: React.ComponentType<DataSourceTablePillWithTooltipProps> =
  React.memo(function DataSourceTablePillWithTooltip({
    dataSourceTableConfig,
    onOpenDataBrowser,
    onPopoverDismiss,
    tooltipProps,
  }) {
    const tableName = getDataframeDisplayName(dataSourceTableConfig);
    const tableId = dataSourceTableConfig.dataSourceTableId;
    const dataConnectionId = dataSourceTableConfig.connectionId;

    const [tooltipOpen, setTooltipOpen] = useState(false);

    const handleTooltipInteraction = useCallback((open: boolean) => {
      setTooltipOpen(open);
    }, []);

    const { dataBrowserActionText } = useTerminology();
    const projectVersionEditable = useProjectVersionEditable();

    const {
      data: tableData,
      error: tableError,
      loading: tableLoading,
    } = useDataSourceTableByIdQuery({
      variables: { dataSourceTableId: tableId },
    });

    const {
      data: dataConnectionData,
      error: dataConnectionError,
      loading: dataConnectionLoading,
    } = useGetDataConnectionInfoForDataSourceTooltipQuery({
      variables: { dataConnectionId: dataConnectionId },
    });
    const loading = tableLoading || dataConnectionLoading;

    const getTableIdPath = useTableNodeIdPathGetter();

    const importedConnections = useDataConnectionHexVersionLinksSelector({
      selector: importedDataConnectionIdsSetSelector,
    });

    const openDataBrowser = useCallback(async () => {
      if (tableData != null && dataConnectionData != null) {
        const nodeIdPath = getTableIdPath({
          tableId,
          schemaId: tableData.dataSourceTableById.dataSourceSchema.id,
          databaseId:
            tableData.dataSourceTableById.dataSourceSchema.dataSourceDatabase
              .id,
          multiDatabase: dataConnectionData.dataConnectionById.isMultiDatabase,
        });

        onOpenDataBrowser({
          dataConnectionId: dataConnectionData ? dataConnectionId : null,
          nodeIdPath,
          tableId: tableId,
        });
      } else {
        // if data connection exists, just open to the data connection,
        // otherwise just open to default state
        onOpenDataBrowser({
          dataConnectionId: dataConnectionData ? dataConnectionId : null,
          nodeIdPath: null,
          tableId: null,
        });
      }
    }, [
      tableData,
      dataConnectionData,
      getTableIdPath,
      tableId,
      onOpenDataBrowser,
      dataConnectionId,
    ]);

    const handleOpenDataBrowserClick = useCallback(
      async (event: React.MouseEvent) => {
        event.stopPropagation();
        onPopoverDismiss?.();
        await openDataBrowser();
      },
      [openDataBrowser, onPopoverDismiss],
    );

    const viewTableDetail = openDataBrowser;
    const handleViewTableDetailAction = useCallback(
      async (event: React.MouseEvent) => {
        trackCounterEvent("codepill.view_table_detail.button");
        event.stopPropagation();
        setTooltipOpen(false);
        await viewTableDetail();
      },
      [viewTableDetail],
    );
    const { dispatchAO } = useHexVersionAOContext();

    const importDataConnection = useCallback(() => {
      dispatchAO(
        CREATE_DATA_CONNECTION_HEX_VERSION_LINK.create({
          dataConnectionHexVersionLinkId:
            uuid() as DataConnectionHexVersionLinkId,
          dataConnectionId,
        }),
      );
    }, [dataConnectionId, dispatchAO]);

    const handleImportDataConnectionClick = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        importDataConnection();
        onPopoverDismiss?.();
      },
      [importDataConnection, onPopoverDismiss],
    );

    const { tooltipContent, tooltipKey, warnIntent } = useMemo(() => {
      if (loading) {
        return {
          tooltipKey: "loading",
          tooltipContent: <HexSpinner />,
          warnIntent: undefined,
        };
      }
      if (dataConnectionError) {
        return {
          tooltipKey: "data-connection-error",
          tooltipContent: (
            <>
              The data connection for this table does not exist. It may have
              been deleted or you do not have access to it.
              {projectVersionEditable && (
                <HexButton
                  intent="primary"
                  onClick={handleOpenDataBrowserClick}
                >
                  {dataBrowserActionText}
                </HexButton>
              )}
            </>
          ),
          warnIntent: Intent.DANGER,
        };
      }
      if (tableError) {
        return {
          tooltipKey: "table-error",
          tooltipContent: (
            <>
              <div>
                The table at{" "}
                <TableName>{dataSourceTableConfig.pathToTable}</TableName> no
                longer exists in the data connection schema. Either refresh your
                schema if this table should exist, or try selecting another
                table.
              </div>
              {projectVersionEditable && (
                <HexButton
                  intent="primary"
                  onClick={handleOpenDataBrowserClick}
                >
                  {dataBrowserActionText}
                </HexButton>
              )}
            </>
          ),
          warnIntent: Intent.WARNING,
        };
      }
      if (tableData && dataConnectionData) {
        if (!importedConnections.has(dataConnectionId)) {
          return {
            tooltipKey: "import-data-connection",
            tooltipContent: (
              <>
                This data connection has not been imported into this project.
                {projectVersionEditable && (
                  <HexButton
                    intent="primary"
                    onClick={handleImportDataConnectionClick}
                  >
                    Import connection
                  </HexButton>
                )}
              </>
            ),
            warnIntent: Intent.DANGER,
          };
        }
        const description =
          tableData.dataSourceTableById.metadata?.magicDescription ||
          tableData.dataSourceTableById.dbtDescriptionMdStripped ||
          tableData.dataSourceTableById.comment;
        return {
          tooltipKey: "view-table-detail",
          tooltipContent: (
            <DataPillTooltipContent
              actionKeyCombo="alt + click"
              actionName="View table detail"
              columns={tableData.dataSourceTableById.columns}
              description={description}
              tableName={tableName}
              onAction={
                projectVersionEditable ? handleViewTableDetailAction : undefined
              }
              onPopoverDismiss={onPopoverDismiss}
            />
          ),
          warnIntent: undefined,
        };
      }
      // we should never get here
      return {
        tooltipKey: "unknown",
        tooltipContent: <></>,
        warnIntent: undefined,
      };
    }, [
      loading,
      dataConnectionError,
      tableError,
      tableData,
      dataConnectionData,
      projectVersionEditable,
      dataBrowserActionText,
      dataSourceTableConfig.pathToTable,
      importedConnections,
      dataConnectionId,
      tableName,
      handleViewTableDetailAction,
      handleImportDataConnectionClick,
      handleOpenDataBrowserClick,
      onPopoverDismiss,
    ]);

    const handleTooltipTargetClick = useCallback(
      async (event: React.MouseEvent) => {
        if (event.altKey && tooltipKey === "view-table-detail") {
          trackCounterEvent("codepill.view_table_detail.shortcut");
          event.stopPropagation();
          await viewTableDetail();
        }
      },
      [viewTableDetail, tooltipKey],
    );

    return (
      <HexTooltip
        content={
          <DataPillTooltipContentWrapper>
            {tooltipContent}
          </DataPillTooltipContentWrapper>
        }
        hoverCloseDelay={200}
        interactionKind="hover"
        isOpen={tooltipOpen}
        maxWidthOverride="none"
        position="bottom-left"
        onInteraction={handleTooltipInteraction}
        {...tooltipProps}
        // eslint-disable-next-line react/jsx-no-bind -- won't hurt in this component
        renderTarget={({ isOpen: _isOpen, ...props }) => (
          // wrapping everything in a flex div makes sure the height stays right surprisingly
          <div
            {...props}
            css="display: flex; overflow: hidden"
            onClick={handleTooltipTargetClick}
          >
            <DataSourceTablePill name={tableName} warningIntent={warnIntent} />
          </div>
        )}
      />
    );
  });

interface DataSourceTablePillProps {
  name: string;
  className?: string;
  warningIntent?: Intent;
  pillTheme?: keyof PillThemes;
  loading?: boolean;
  iconOverride?: React.ReactNode;
}

export const DataSourceTablePill: React.ComponentType<DataSourceTablePillProps> =
  React.memo(function DataSourceTablePill({
    className,
    iconOverride,
    loading,
    name,
    pillTheme,
    warningIntent,
  }) {
    return (
      <DataSourceTableWrapper className={className} pillTheme={pillTheme}>
        {loading ? (
          <HexSpinner />
        ) : warningIntent ? (
          <StyledWarningIcon intent={warningIntent} />
        ) : (
          iconOverride ?? <DataSourceTableIcon />
        )}
        <DataSourceName>{name}</DataSourceName>
      </DataSourceTableWrapper>
    );
  });

const DataSourceName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
